import { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Input, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { captureUsage, getUsageAggregation } from '@/api/usageAggregation';
import type { ModuleUsage, UsageAggregation } from '@/api/usageAggregation';
import { normalizeApiError } from '@/api/errors';
import { useAuth } from '@/auth/OlAuthContext';
import { formatAbsolute } from '@/lib/time';

const { Title, Paragraph, Text } = Typography;

const MODULE_NAMES: Record<string, string> = {
  onelattice: 'OneLattice',
  procureflow: 'ProcureFlow',
  graphintel: 'GraphIntel',
  verityx: 'VerityX',
  vcip: 'vCIP',
  vsite: 'vSite',
  trident: 'Trident',
};

const UNIT_NAMES: Record<string, string> = {
  capabilityCalls: 'Capability calls',
  capabilityCallsNotBilled: 'Calls not billed',
  capabilityVendorUnits: 'Vendor units',
  capabilityCostMicrosUsd: 'Cost (USD micros)',
  agentCaseCount: 'Agent cases',
};

const unitName = (unit: string) => UNIT_NAMES[unit] ?? unit;
const thisMonth = () => new Date().toISOString().slice(0, 7);
const signed = (n: number) => (n > 0 ? `+${n.toLocaleString()}` : n.toLocaleString());

const RECONCILIATION: Record<string, { label: string; color?: string }> = {
  MATCHES: { label: 'Matches its meter', color: 'green' },
  DIFFERS: { label: 'Differs from its meter', color: 'gold' },
  NOT_CAPTURED: { label: 'Not captured yet' },
  NOT_CONNECTED: { label: '—' },
  UNAVAILABLE: { label: 'Could not read', color: 'red' },
};

function Usage({ module }: { module: ModuleUsage }) {
  if (module.state !== 'CONNECTED') {
    return (
      <div data-testid={`usage-absent-${module.module}`}>
        <Tag color={module.state === 'UNAVAILABLE' ? 'red' : undefined}>
          {module.state === 'UNAVAILABLE' ? 'Could not read' : 'Not connected'}
        </Tag>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {module.reason}
        </Text>
      </div>
    );
  }
  const totals = Object.entries(module.totals ?? {});
  if (totals.length === 0) {
    return <Text type="secondary">No usage recorded this month.</Text>;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 18px' }}>
      {totals.map(([unit, quantity]) => (
        <span key={unit} style={{ fontSize: 12.5 }}>
          <Text type="secondary">{unitName(unit)}: </Text>
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{quantity.toLocaleString()}</span>
        </span>
      ))}
    </div>
  );
}

function Differences({ module, monthOpen }: { module: ModuleUsage; monthOpen: boolean }) {
  const { reconciliation, crossCheck } = module;
  const lines = module.lines ?? [];
  const byProvider = lines.filter((l) => l.unit === 'capabilityCalls' && l.dimension);
  return (
    <div style={{ fontSize: 12.5 }}>
      {reconciliation.result === 'DIFFERS' && (
        <div style={{ marginBottom: 10 }} data-testid={`usage-differences-${module.module}`}>
          <Text strong>Captured against the meter now.</Text>
          {monthOpen && <Text type="secondary"> This month is still open, so a capture falls behind as usage continues.</Text>}
          {reconciliation.differences.map((d) => (
            <div key={`${d.unit}|${d.dimension}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
              {unitName(d.unit)} {d.dimension && <span className="ol-mono" style={{ fontSize: 12 }}>{d.dimension}</span>}: captured{' '}
              {d.aggregated.toLocaleString()}, meter {d.metered.toLocaleString()} ({signed(d.difference)})
            </div>
          ))}
        </div>
      )}
      {reconciliation.capturedAt && (
        <div style={{ marginBottom: 10 }}>
          <Text type="secondary">Last captured {formatAbsolute(reconciliation.capturedAt)}</Text>
        </div>
      )}
      {crossCheck && (
        <div style={{ marginBottom: 10 }} data-testid={`usage-crosscheck-${module.module}`}>
          <Text strong>
            Meter against the call log: {crossCheck.result === 'MATCHES' ? 'matches' : 'differs'}
          </Text>
          {crossCheck.differences.map((d) => (
            <div key={d.dimension} style={{ fontVariantNumeric: 'tabular-nums' }}>
              <span className="ol-mono" style={{ fontSize: 12 }}>{d.dimension}</span>: meter {d.meter.toLocaleString()}, call log{' '}
              {d.ledger.toLocaleString()} ({signed(d.difference)})
            </div>
          ))}
          {crossCheck.result === 'DIFFERS' && (
            <Text type="secondary">
              Calls finished before this meter started are in the call log only. A key used again after its 72-hour
              window is two calls in the meter and one row in the call log.
            </Text>
          )}
        </div>
      )}
      {byProvider.length > 0 && (
        <div>
          <Text strong>Capability calls by provider</Text>
          {byProvider.map((l) => (
            <div key={l.dimension} style={{ fontVariantNumeric: 'tabular-nums' }}>
              <span className="ol-mono" style={{ fontSize: 12 }}>{l.dimension}</span>: {l.quantity.toLocaleString()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** API-09: this hub's month, per module, from each product's own meter. A module with no meter shows no number. */
export function UsageByModule() {
  const { message } = App.useApp();
  const { user } = useAuth();
  const canCapture = (user?.roles ?? []).some((r) => r === 'hub_admin' || r === 'hub_operator');
  const [month, setMonth] = useState(thisMonth());
  const [view, setView] = useState<UsageAggregation | null>(null);
  const [loading, setLoading] = useState(true);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setView(await getUsageAggregation(month));
    } catch (e) {
      setError(normalizeApiError(e).message);
      setView(null);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    void load();
  }, [load]);

  const capture = async () => {
    setCapturing(true);
    try {
      setView(await captureUsage(month));
      message.success(`Captured usage for ${month}`);
    } catch (e) {
      message.error(normalizeApiError(e).message);
    } finally {
      setCapturing(false);
    }
  };

  const columns: ColumnsType<ModuleUsage> = [
    {
      title: 'Module',
      key: 'module',
      width: 130,
      render: (_, m) => MODULE_NAMES[m.module] ?? m.module,
    },
    { title: 'Usage', key: 'usage', render: (_, m) => <Usage module={m} /> },
    {
      title: 'Reconciliation',
      key: 'reconciliation',
      width: 190,
      render: (_, m) => {
        const r = RECONCILIATION[m.reconciliation.result];
        return r.color || m.reconciliation.result === 'NOT_CAPTURED' ? (
          <Tag color={r.color} data-testid={`usage-result-${m.module}`}>
            {r.label}
          </Tag>
        ) : (
          <span data-testid={`usage-result-${m.module}`}>{r.label}</span>
        );
      },
    },
  ];

  const expandable = (m: ModuleUsage) =>
    m.state === 'CONNECTED' && (m.reconciliation.result === 'DIFFERS' || !!m.reconciliation.capturedAt || !!m.crossCheck);

  return (
    <div style={{ margin: '8px 0 28px' }} data-testid="usage-by-module">
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <Title level={5} style={{ margin: 0 }}>
            Usage by module
          </Title>
          <Paragraph type="secondary" style={{ margin: '4px 0 0', fontSize: 13 }}>
            Each product&apos;s own meter for one calendar month (UTC). A capture records what the meters answer, and
            later reads are compared with it.
          </Paragraph>
        </div>
        <label style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)' }}>
          <div style={{ marginBottom: 3 }}>Month</div>
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            aria-label="Usage month"
            data-testid="usage-month"
            style={{ width: 160 }}
          />
        </label>
        {canCapture && (
          <Button onClick={() => void capture()} loading={capturing} data-testid="usage-capture">
            Capture month
          </Button>
        )}
      </div>
      {error && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message="Usage request failed" description={error} />
      )}
      <Table<ModuleUsage>
        rowKey="module"
        columns={columns}
        dataSource={view?.modules ?? []}
        loading={loading}
        size="small"
        pagination={false}
        expandable={{
          rowExpandable: expandable,
          expandedRowRender: (m) => <Differences module={m} monthOpen={!!view?.monthOpen} />,
        }}
      />
    </div>
  );
}
